import { default as LruCache } from 'lru-cache';

import numeral from 'numeral';

import { ILieuucationMapProperties } from '@lieuu/core';
import { HigherLowerNone, IMapFilters, NumberRange } from '@reducers';

interface IWeightValue {
  value: number;
  weight: number;
}

interface IWeights {
  ageWeight: IWeightValue;
  incomeWeight: IWeightValue;
  educationWeight: IWeightValue;
  homeValueWeight: IWeightValue;
  rentWeight: IWeightValue;
  airbnbWeight: IWeightValue;
  crimesWeight: IWeightValue;
  schoolsWeight: IWeightValue;
}

const demoWeights: IWeights = {
  ageWeight: { value: -1, weight: -1 },
  incomeWeight: { value: -1, weight: -1 },
  educationWeight: { value: -1, weight: -1 },
  homeValueWeight: { value: -1, weight: -1 },
  rentWeight: { value: -1, weight: -1 },
  airbnbWeight: { value: -1, weight: -1 },
  crimesWeight: { value: -1, weight: -1 },
  schoolsWeight: { value: -1, weight: -1 }
};

const cache = new LruCache<string, number>({ max: 5000 });

const isNaNOrNegative = (value: number) => isNaN(value) || value < 0;

const defaultUndefinedScore = 0.6;

const getMaxCappedScore = (value, cap) =>
  isNaNOrNegative(value)
    ? defaultUndefinedScore
    : value >= cap
    ? 1.0
    : value / cap;

const getClosenessScore = (v1, v2) => {
  const score = Math.abs(v1 - v2) / ((v1 + v2) / 2);

  return score < 1 ? score : 1;
};

const minCap = (v, minCap) => (v < minCap ? minCap : v);

const partFactors = 1 / Object.keys(demoWeights).length;

const getScoreForRange = (
  value: number,
  numberRange: NumberRange,
  params?: { skewHigher?: boolean; skewLower?: boolean }
): number => {
  if (
    numberRange.user.min === numberRange.bounds.minMax.min &&
    numberRange.user.max === numberRange.bounds.minMax.max
  ) {
    return 1;
  }
  if (value === -1) {
    return -1;
  }
  if (
    (value >= numberRange.user.min && value <= numberRange.user.max) ||
    (value < numberRange.user.min &&
      numberRange.user.min === numberRange.bounds.minMax.min) ||
    (value > numberRange.user.max &&
      numberRange.user.max === numberRange.bounds.minMax.max)
  ) {
    const middleValue =
      numberRange.user.max - (numberRange.user.max - numberRange.user.min) / 2;

    const score = minCap(
      getClosenessScore(
        value,
        params && params.skewHigher
          ? numberRange.user.max -
              (numberRange.user.max - numberRange.user.min) / 4
          : params && params.skewLower
          ? numberRange.user.max -
            (3 * (numberRange.user.max - numberRange.user.min)) / 4
          : middleValue
      ),
      0.5
    );

    return score;
  }

  return -1;
};

const getPropertiesScore = (
  properties: ILieuucationMapProperties,
  filters: IMapFilters
) => {
  const {
    preferIncome,
    preferHomePrice,
    preferRentPrice,
    preferCrowdAge,
    preferEducation = HigherLowerNone.NoPref,
    preferHigherAirbnbAbility = HigherLowerNone.NoPref,
    preferCrimeRate = HigherLowerNone.NoPref,
    preferSchoolRatings = HigherLowerNone.NoPref
  } = filters;

  const age = properties['marketProfile:typicalAge:latest'];
  const income = properties['marketProfile:income:household:latest'];
  const education = properties['marketProfile:bachelorsOrHigher'];

  const homeValue = properties['housing:homeValue:latest'];
  const rent = properties['housing:grossRent:latest'];

  const airbnbRoomRate = properties['shortTermRentals:airbnb:nightly:roomRate'];
  const wholeHomeRate =
    properties['shortTermRentals:airbnb:nightly:wholeHomeRate'];

  const nonViolentCrimes = properties['safety:crimes:nonViolent:totalCount'];
  const violentCrimes = properties['safety:crimes:violent:totalCount'];

  const schoolsRating = properties['schools:assignedSchoolsRating:overall'];

  const weights: IWeights = {
    ageWeight: {
      value: getScoreForRange(age, preferCrowdAge, { skewLower: true }),
      weight: partFactors
    },

    incomeWeight: {
      value: getScoreForRange(income, preferIncome, { skewHigher: true }),
      weight: partFactors
    },

    educationWeight: {
      value:
        preferEducation === HigherLowerNone.NoPref
          ? 1
          : preferEducation === HigherLowerNone.Higher
          ? education > 0.9
            ? education
            : education + 0.1
          : 1 - education,
      weight: partFactors
    },

    homeValueWeight: {
      value: getScoreForRange(homeValue, preferHomePrice, { skewHigher: true }),
      weight: partFactors
    },

    rentWeight: {
      value: getScoreForRange(rent, preferRentPrice, { skewHigher: true }),
      weight: partFactors
    },

    airbnbWeight: {
      value:
        preferHigherAirbnbAbility === HigherLowerNone.NoPref
          ? 1
          : getMaxCappedScore(airbnbRoomRate, 80) / 2 +
            getMaxCappedScore(wholeHomeRate, 170) / 2,
      weight: partFactors
    },

    crimesWeight: {
      value:
        preferCrimeRate === HigherLowerNone.NoPref
          ? 1
          : 1 - getMaxCappedScore(nonViolentCrimes + violentCrimes, 4),
      weight: partFactors
    },

    schoolsWeight: {
      value:
        preferSchoolRatings === HigherLowerNone.NoPref
          ? 1
          : getMaxCappedScore(schoolsRating, 10),
      weight: partFactors
    }
  };

  // console.log({ weights });

  const values: IWeightValue[] = Object.values(weights);

  const score = values
    .filter((e) => !isNaNOrNegative(e.value))
    .reduce((prev, curr) => curr.value * curr.weight + prev, 0);

  // console.log({ score });

  return score;
};

export const getNumericalScore = (
  properties: ILieuucationMapProperties,
  filters: IMapFilters
): number => {
  const id = `${properties.internalId}:${JSON.stringify(filters)}`;

  // console.log({ name: properties.name });

  const cacheValue = cache.get(id);

  if (cacheValue) {
    return cacheValue;
  }

  const propsScore = getPropertiesScore(properties, filters);

  const score =
    propsScore === -1
      ? -1
      : propsScore > 1
      ? 1
      : propsScore < 0
      ? 0
      : propsScore; // max out at 100% and 0%

  cache.set(id, score);

  return score;
};

export const getTextFromScore = (score: number) =>
  numeral(score === -1 ? 0 : score).format('0%');
